import { useQuery } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { APIError, api, jsonBody } from "../api/client";
import { Card, CardHeader, ErrorPanel, Field, FormActions, Notice } from "../ui/components";
import { SkeletonCard, SkeletonText } from "../ui/skeletons";
import { useAuth } from "./AuthProvider";

export interface SensitiveActionStatus {
  enabled: boolean;
  recoveryCodesRemaining: number;
  recentlyVerified: boolean;
  verifiedUntil?: string;
}

export interface SensitiveActionController {
  unlocked: boolean;
  /** Whether two-step verification is set up on the account; undefined until the status loads. */
  mfaEnabled: boolean | undefined;
  markProtectedError(error: unknown): boolean;
  view: ReactNode;
}

export interface SensitiveActionOptions {
  setupTitle?: string;
  lockedTitle?: string;
  lockedDescription?: string;
}

const sensitiveActionKey = ["auth", "mfa"] as const;

export function useSensitiveAction(
  options: SensitiveActionOptions = {},
): SensitiveActionController {
  const auth = useAuth();
  const status = useQuery({
    queryKey: sensitiveActionKey,
    queryFn: () => api<SensitiveActionStatus>("/api/v1/auth/mfa"),
  });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const setupPath = auth.session?.selectedMembership ? "/settings/security" : "/security";
  const [forcedLocked, setForcedLocked] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const verifiedUntil = status.data?.verifiedUntil
    ? new Date(status.data.verifiedUntil).getTime()
    : 0;
  const unlocked = !forcedLocked && Boolean(status.data?.recentlyVerified) && verifiedUntil > now;

  useEffect(() => {
    if (!status.data?.recentlyVerified || verifiedUntil <= Date.now()) return;
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(verifiedUntil - Date.now() + 25, 2_147_483_647),
    );
    return () => window.clearTimeout(timer);
  }, [status.data?.recentlyVerified, verifiedUntil]);

  async function verify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const code = String(form.get("code") ?? "").trim();
    const recoveryCode = String(form.get("recoveryCode") ?? "").trim();
    setWorking(true);
    setError(undefined);
    try {
      await api<void>("/api/v1/auth/mfa/step-up", {
        method: "POST",
        body: jsonBody(code ? { code } : { recoveryCode }),
      });
      formElement.reset();
      setForcedLocked(false);
      setNow(Date.now());
      await Promise.all([status.refetch(), auth.refresh()]);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  function markProtectedError(caught: unknown): boolean {
    if (!(caught instanceof APIError) || caught.code !== "step_up_required") return false;
    setForcedLocked(true);
    setError(
      new Error("Your confirmation has expired. Enter a new code, then try the change again."),
    );
    void status.refetch();
    return true;
  }

  let view: ReactNode;
  if (status.isLoading) {
    view = (
      <SkeletonCard>
        <SkeletonText lines={2} />
      </SkeletonCard>
    );
  } else if (status.error) {
    view = <ErrorPanel error={status.error} />;
  } else if (unlocked) {
    view = (
      <Notice tone="success" title="Protected changes ready">
        You can make protected changes until {new Date(verifiedUntil).toLocaleTimeString()}.
      </Notice>
    );
  } else if (!status.data?.enabled) {
    view = (
      <Notice
        tone="warning"
        title={options.setupTitle ?? "Set up two-step verification to continue"}
      >
        <span>
          For your security, <Link to={setupPath}>set up an authenticator app</Link> before making
          this change.
        </span>
      </Notice>
    );
  } else {
    view = (
      <Card>
        <CardHeader
          title={options.lockedTitle ?? "Confirm it’s you"}
          description={
            options.lockedDescription ??
            "Enter a code before making this change. This extra check stays active for 15 minutes."
          }
        />
        {error ? <ErrorPanel error={error} /> : null}
        <form className="form-grid" onSubmit={verify}>
          <Field label="Authenticator code" hint="Use either this field or a recovery code.">
            <input
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
            />
          </Field>
          <Field label="Recovery code">
            <input name="recoveryCode" autoComplete="one-time-code" />
          </Field>
          <div className="full">
            <FormActions submitting={working} submitLabel="Confirm for 15 minutes" />
          </div>
        </form>
      </Card>
    );
  }

  return { unlocked, mfaEnabled: status.data?.enabled, markProtectedError, view };
}
